import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  //Button,
  IconButton,
  Divider,
  Paper,
  InputAdornment,
  ClickAwayListener
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

interface EditableStepContentProps {
  title: string;
  content: React.ReactNode;
  cost: number;
  onCostChange?: (newCost: number) => void;
  showCost?: boolean;
}

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
  position: 'relative',
}));

const CostPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  background: 'rgba(0, 204, 153, 0.1)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(0, 204, 153, 0.3)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(1),
}));

const EditableStepContent: React.FC<EditableStepContentProps> = ({
  title,
  content,
  cost,
  onCostChange,
  showCost = true
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editableCost, setEditableCost] = useState(cost);
  const inputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    setEditableCost(cost);
  }, [cost]);
  
  const handleEdit = () => {
    setIsEditing(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };
  
  const handleSave = () => {
    if (onCostChange && editableCost !== cost) {
      onCostChange(editableCost);
    }
    setIsEditing(false);
  };
  
  const handleCancel = () => {
    setEditableCost(cost);
    setIsEditing(false);
  };
  
  const handleCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setEditableCost(isNaN(value) ? 0 : value);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };
  
  const handleClickAway = () => {
    if (isEditing) {
      handleSave();
    }
  };
  
  return (
    <ContentBox>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ color: '#fff' }}>
          {title}
        </Typography>
        
        {!isEditing ? (
          <IconButton size="small" onClick={handleEdit} sx={{ color: '#00CC99' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        ) : (
          <Box>
            <IconButton size="small" onClick={handleSave} sx={{ color: '#00CC99' }}>
              <SaveIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleCancel} sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
      
      {content}
      
      {showCost && (
        <>
          <Divider sx={{ my: 1, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />
          
          <CostPaper>
            <Typography variant="subtitle2" sx={{ color: '#00CC99' }}>
              Cost Estimate
            </Typography>
            
            {isEditing ? (
              <ClickAwayListener onClickAway={handleClickAway}>
                <TextField
                  value={editableCost}
                  onChange={handleCostChange}
                  onKeyDown={handleKeyDown}
                  variant="outlined"
                  size="small"
                  type="number"
                  inputRef={inputRef}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    sx: { color: '#fff', width: '120px' }
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 204, 153, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 204, 153, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00CC99',
                      },
                    }
                  }}
                />
              </ClickAwayListener>
            ) : (
              <Typography variant="h6" sx={{ color: '#00CC99', fontWeight: 600 }}>
                ${editableCost.toLocaleString()}
              </Typography>
            )}
          </CostPaper>
        </>
      )}
    </ContentBox>
  );
};

export default EditableStepContent; 